import { ColumnDef } from "@tanstack/react-table";
import { Campaign } from "@/api/campaigns";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { Clock, Pencil, ExternalLink, Loader2, ArrowUpDown, Copy, Sliders } from "lucide-react";
import { FlightStatusBadge } from "@/components/campaigns/FlightStatusBadge";

interface CampaignColumnsProps {
  campaignStatuses: any[];
  campaignStatusMap: Map<number, any>;
  updatingCampaignId: number | null;
  onStatusChange: (campaignId: number, statusId: number) => void;
  onEditClick: (campaignId: number) => void;
  onDetailedEditClick: (campaignId: number) => void;
  onViewClick: (campaignId: number) => void;
  onCloneClick?: (campaign: { id: number; name: string; retailerId: number | null }) => void;
}

export function createCampaignColumns({
  campaignStatuses,
  campaignStatusMap,
  updatingCampaignId,
  onStatusChange,
  onEditClick,
  onDetailedEditClick,
  onViewClick,
  onCloneClick,
}: CampaignColumnsProps): ColumnDef<Campaign>[] {
  const formatDate = (dateString?: string | null) => {
    if (!dateString) return 'Not set';
    const date = new Date(dateString);
    if (Number.isNaN(date.getTime())) {
      return 'Not set';
    }
    return `${String(date.getDate()).padStart(2, '0')}/${String(date.getMonth() + 1).padStart(2, '0')}/${date.getFullYear()}`;
  };

  const getCycleLabel = (campaign: Campaign) => {
    if (campaign.cycleName && campaign.cycleName.trim().length > 0) {
      const name = campaign.cycleName.trim();
      return /^\d+$/.test(name) ? `Cycle ${name}` : name;
    }
    if (typeof campaign.cycleId === 'number') {
      return `Cycle ${campaign.cycleId}`;
    }
    return '—';
  };

  const resolveStatusDisplay = (campaign: Campaign): { name: string; dotColor: string } => {
    if (typeof campaign.statusId === 'number') {
      const st = campaignStatusMap.get(campaign.statusId);
      if (st) {
        return { name: st.name || String(campaign.status || 'Draft'), dotColor: st.color || '#6B7280' };
      }
    }
    const name = campaign.status || 'Draft';
    const lower = String(name).toLowerCase();
    const dotColor = lower === 'active' ? '#22c55e' : lower === 'completed' ? '#3b82f6' : lower === 'cancelled' ? '#ef4444' : '#9ca3af';
    return { name, dotColor };
  };

  return [
    {
      id: "select",
      header: ({ table }) => (
        <Checkbox
          checked={
            table.getIsAllPageRowsSelected() ||
            (table.getIsSomePageRowsSelected() && "indeterminate")
          }
          onCheckedChange={(value) => table.toggleAllPageRowsSelected(!!value)}
          aria-label="Select all campaigns on this page"
        />
      ),
      cell: ({ row }) => (
        <Checkbox
          checked={row.getIsSelected()}
          onCheckedChange={(value) => row.toggleSelected(!!value)}
          aria-label={`Select campaign ${row.original.name}`}
        />
      ),
      enableSorting: false,
      enableHiding: false,
    },
    {
      accessorKey: "cycleName",
      header: ({ column }) => {
        return (
          <Button
            variant="ghost"
            onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
            className="h-8 px-2"
          >
            Cycle Name
            <ArrowUpDown className="ml-2 h-4 w-4" />
          </Button>
        );
      },
      cell: ({ row }) => {
        return (
          <div className="text-muted-foreground">
            {getCycleLabel(row.original)}
          </div>
        );
      },
    },
    {
      accessorKey: "name",
      header: ({ column }) => {
        return (
          <Button
            variant="ghost"
            onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
            className="h-8 px-2"
          >
            Campaign Name
            <ArrowUpDown className="ml-2 h-4 w-4" />
          </Button>
        );
      },
      cell: ({ row }) => {
        return (
          <button
            onClick={() => onViewClick(row.original.id)}
            className="text-blue-600 hover:text-blue-800 hover:underline text-left font-medium focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 rounded"
          >
            {row.original.name}
          </button>
        );
      },
    },
    {
      accessorKey: "advertiserName",
      header: ({ column }) => {
        return (
          <Button
            variant="ghost"
            onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
            className="h-8 px-2"
          >
            Account
            <ArrowUpDown className="ml-2 h-4 w-4" />
          </Button>
        );
      },
      cell: ({ row }) => {
        const advertiserName = row.original.advertiserName;
        const advertiserId = row.original.advertiserId;
        if (!advertiserName) {
          return <div className="text-muted-foreground">—</div>;
        }
        return advertiserId ? (
          <a
            href={`/accounts/${advertiserId}`}
            className="text-blue-600 hover:text-blue-800 hover:underline"
          >
            {advertiserName}
          </a>
        ) : (
          <div className="text-muted-foreground">{advertiserName}</div>
        );
      },
    },
    // Phase 4.7A: Booking column for hierarchical cohesion
    {
      accessorKey: "bookingName",
      header: ({ column }) => {
        return (
          <Button
            variant="ghost"
            onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
            className="h-8 px-2"
          >
            Booking
            <ArrowUpDown className="ml-2 h-4 w-4" />
          </Button>
        );
      },
      cell: ({ row }) => {
        const bookingName = row.original.bookingName;
        const bookingId = row.original.bookingId;
        if (!bookingId) {
          // Phase 4.7A: Distinct "Unassigned" treatment for orphaned campaigns
          return (
            <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-400">
              Unassigned
            </span>
          );
        }
        return (
          <a
            href={`/admin/bookings/${bookingId}`}
            className="text-blue-600 hover:text-blue-800 hover:underline"
          >
            {bookingName || `Booking #${bookingId}`}
          </a>
        );
      },
    },
    {
      accessorKey: "id",
      header: ({ column }) => {
        return (
          <Button
            variant="ghost"
            onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
            className="h-8 px-2"
          >
            Campaign ID
            <ArrowUpDown className="ml-2 h-4 w-4" />
          </Button>
        );
      },
      cell: ({ row }) => {
        return (
          <div className="text-muted-foreground">
            {row.original.id}
          </div>
        );
      },
    },
    {
      accessorKey: "status",
      header: "Status",
      cell: ({ row }) => {
        const campaign = row.original;
        const statusDisplay = resolveStatusDisplay(campaign);
        const isUpdating = updatingCampaignId === campaign.id;

        return (
          <div className="flex items-center gap-2">
            <Select
              value={campaign.statusId?.toString() || '1'}
              onValueChange={(value) => {
                const statusId = parseInt(value, 10);
                if (isNaN(statusId) || statusId === campaign.statusId) return;
                onStatusChange(campaign.id, statusId);
              }}
              disabled={isUpdating}
            >
              <SelectTrigger className="h-8 w-[160px]">
                {isUpdating ? (
                  <div className="flex items-center gap-2">
                    <Loader2 className="h-3 w-3 animate-spin" />
                    <span>Updating...</span>
                  </div>
                ) : (
                  <SelectValue>
                    <div className="flex items-center gap-2">
                      <span
                        className="inline-block w-2 h-2 rounded-full"
                        style={{ backgroundColor: statusDisplay.dotColor }}
                      />
                      <span className="capitalize">{statusDisplay.name}</span>
                    </div>
                  </SelectValue>
                )}
              </SelectTrigger>
              <SelectContent>
                {campaignStatuses
                  .filter((s: any) => s.isActive !== false)
                  .sort((a: any, b: any) => (a.sortOrder || 0) - (b.sortOrder || 0))
                  .map((status: any) => (
                    <SelectItem key={status.id} value={status.id.toString()}>
                      <div className="flex items-center gap-2">
                        <span
                          className="inline-block w-2 h-2 rounded-full"
                          style={{ backgroundColor: status.color || '#6B7280' }}
                        />
                        <span className="capitalize">{status.name}</span>
                      </div>
                    </SelectItem>
                  ))}
              </SelectContent>
            </Select>
          </div>
        );
      },
    },
    {
      accessorKey: "flightStatus",
      header: "In Field",
      cell: ({ row }) => <FlightStatusBadge status={row.original.flightStatus} />,
    },
    {
      accessorKey: "createdAt",
      header: ({ column }) => {
        return (
          <Button
            variant="ghost"
            onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
            className="h-8 px-2"
          >
            Last Updated
            <ArrowUpDown className="ml-2 h-4 w-4" />
          </Button>
        );
      },
      cell: ({ row }) => {
        return (
          <div className="text-muted-foreground">
            {formatDate(row.original.createdAt)}
          </div>
        );
      },
    },
    {
      id: "actions",
      header: () => <div className="text-right">Actions</div>,
      cell: ({ row }) => {
        const campaign = row.original;
        const isDraft = Number(campaign.statusId) === 1 ||
          String(campaign.status ?? '').toLowerCase() === 'draft';

        return (
          <div className="flex items-center justify-end gap-2">
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => onEditClick(campaign.id)}
                    className="h-8 px-2"
                  >
                    <Pencil className="w-4 h-4" />
                    <span className="sr-only">Quick edit campaign</span>
                  </Button>
                </TooltipTrigger>
                <TooltipContent>
                  <div className="space-y-1">
                    <p className="font-semibold">Quick edit</p>
                    <p className="text-xs">{isDraft ? 'Make small, safe changes like names or notes. Does not change stores, formats, operators, or quantities.' : 'Only draft campaigns can be edited. Change status to Draft first.'}</p>
                  </div>
                </TooltipContent>
              </Tooltip>

              {isDraft && (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => onDetailedEditClick(campaign.id)}
                      className="h-8 px-2"
                    >
                      <Sliders className="w-4 h-4" />
                      <span className="sr-only">Detailed edit</span>
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>
                    <div className="space-y-1">
                      <p className="font-semibold">Detailed edit</p>
                      <p className="text-xs">{isDraft ? 'Manually edit the campaign setup — stores, formats, quantities, and operators. Use this when you need to fix specific rows.' : 'Only draft campaigns can be edited. Change status to Draft first.'}</p>
                    </div>
                  </TooltipContent>
                </Tooltip>
              )}

              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => onViewClick(campaign.id)}
                    className="h-8 px-2"
                  >
                    <ExternalLink className="w-4 h-4" />
                    <span className="sr-only">View campaign details</span>
                  </Button>
                </TooltipTrigger>
                <TooltipContent>
                  <p>View campaign details</p>
                </TooltipContent>
              </Tooltip>

              {onCloneClick && (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => onCloneClick({ id: campaign.id, name: campaign.name, retailerId: campaign.retailerId ?? null })}
                      className="h-8 px-2"
                    >
                      <Copy className="w-4 h-4" />
                      <span className="sr-only">Clone campaign</span>
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>Clone campaign to another cycle</p>
                  </TooltipContent>
                </Tooltip>
              )}
            </TooltipProvider>
          </div>
        );
      },
    },
  ];
}
